import { AxiosForLegacyBackend } from "../../utils/helpers/AxiosForLegacyBackend";
import AxiosRaw from "axios";
import { type POSADeliveryJavaDTO } from "@tootsweet/model/lottery/POSADelivery";
import { CustomPOSA_Javascript_DTO } from "@tootsweet/model/entities/saas/CustomPOSA";
import UploadedPOSA from "@tootsweet/model/entities/UploadedPOSA";
import type BillDTO from "@tootsweet/model/datatransfer/BillDTO";
import CONFIG from "../../model/TSConfig";
import { Colors } from "kadow-jetpack-model";
import { POSATypeJavascriptDTO } from "@tootsweet/model/lottery/POSAType";

export type POSADeliveryWithBill = POSADeliveryJavaDTO & {
  bill?: BillDTO;
};

export default class POSAApi {
  public static getPOSAList = async (
    businessUserId: string,
    aggregate: boolean
  ): Promise<POSADeliveryWithBill[]> => {
    const res = await AxiosForLegacyBackend({
      method: "GET",
      url: "/posa",
      params: { businessUserId, aggregate }
    });
    return res.data;
  };

  public static getOne = async (
    id: string,
    businessUserId: string
  ): Promise<POSADeliveryWithBill> => {
    const res = await AxiosForLegacyBackend({
      method: "GET",
      url: "/posa/one",
      params: { id, businessUserId }
    });
    return res.data;
  };

  public static setDesigned = async (
    id: string | null,
    fd: FormData
  ): Promise<POSADeliveryJavaDTO | null> => {
    if (!id) {
      return null;
    }
    const res = await AxiosForLegacyBackend({
      method: "POST",
      url: `/posa/setDesigned/${id}`,
      headers: {
        "Content-Type": "multipart/form-data"
      },
      data: fd
    });
    return res.data;
  };

  public static validate = async (
    id: string | null
  ): Promise<POSADeliveryJavaDTO | null> => {
    if (!id) {
      return null;
    }
    const res = await AxiosForLegacyBackend({
      method: "PUT",
      url: `/posa/validate/${id}`
    });
    return res.data;
  };

  public static doNotValidate = async (
    id: string,
    comment: string
  ): Promise<POSADeliveryJavaDTO> => {
    const res = await AxiosForLegacyBackend({
      method: "PUT",
      url: `/posa/do_not_validate/${id}`,
      params: {
        comment
      }
    });
    return res.data;
  };

  public static uploadPOSAPng = async (
    trackingID: string,
    pngDataBase64: string,
    posa: POSATypeJavascriptDTO,
    colors: Colors,
    token: string
  ): Promise<{
    success: boolean;
    url: string;
    customPOSAs: CustomPOSA_Javascript_DTO[];
  }> => {
    const res = await AxiosRaw({
      method: "POST",
      url: `${CONFIG.saasBackendUrl}/posa/${trackingID}/upload`,
      headers: {
        Authorization: `Bearer ${token}`
      },
      data: {
        pngData: pngDataBase64,
        posaTypeId: posa._id,
        format: posa.format,
        colors
      }
    });
    return res.data;
  };

  public static uploadExistingPOSA = async (
    trackingID: string | undefined,
    type: string,
    file: File,
    token: string | undefined
  ): Promise<UploadedPOSA> => {
    const fd = new FormData();
    fd.append("file", file);

    const res = await AxiosRaw({
      method: "POST",
      url: `${CONFIG.saasBackendUrl}/posa/${trackingID}/upload-existing-posa`,
      headers: {
        Authorization: `Bearer ${token}`
      },
      params: { type },
      data: fd
    });

    return res.data;
  };

  public static deleteCustomPOSA = async (
    _id: string,
    token: string
  ): Promise<void> => {
    await AxiosRaw({
      method: "DELETE",
      url: `${CONFIG.saasBackendUrl}/posa/${_id}`,
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
  };

  public static getUploadedPOSA = async (
    trackingID: string,
    token: string
  ): Promise<UploadedPOSA[]> => {
    const res = await AxiosRaw({
      method: "GET",
      url: `${CONFIG.saasBackendUrl}/posa/${trackingID}/uploaded-posa`,
      headers: {
        Authorization: `Bearer ${token}`
      }
    });

    return res.data;
  };

  public static updateUploadedPOSA = async (
    trackingID: string,
    data: Partial<UploadedPOSA>,
    token: string
  ): Promise<UploadedPOSA> => {
    const res = await AxiosRaw({
      method: "PUT",
      url: `${CONFIG.saasBackendUrl}/posa/${trackingID}/update-uploaded-posa`,
      headers: {
        Authorization: `Bearer ${token}`
      },
      data
    });

    return res.data;
  };
}
